import React, { useState, useEffect, useRef } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import { CheckIcon, PencilIcon } from '@heroicons/react/24/solid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, ExternalLink, Globe } from "lucide-react";
import { formatDate } from '../utils/dateFormatter';

const PageModal: React.FC<{ isOpen: boolean; closeModal: () => void; pageId: string }> = ({ isOpen, closeModal, pageId }) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<any>(null);
  const [editingStarter, setEditingStarter] = useState<string | null>(null);
  const [editedStarters, setEditedStarters] = useState<{ id: string; value: string }[]>([]);
  const [editingPopUp, setEditingPopUp] = useState(false);
  const [editedPopUp, setEditedPopUp] = useState('');
  const [showChunks, setShowChunks] = useState(false);
  const { get, put } = useApi();

  const popUpRef = useRef<HTMLDivElement>(null);
  const starterRefs = useRef<{ [id: string]: HTMLLIElement | null }>({});

  useEffect(() => {
    const fetchPageDetails = async () => {
      try {
        setLoading(true);
        const data = await get(`/pages/${pageId}`);
        setPage(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching page details:', error);
        toast.error('Error fetching page details', toastConfig);
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPageDetails();
    }

  }, [isOpen, pageId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingPopUp && popUpRef.current && !popUpRef.current.contains(event.target as Node)) {
        setEditingPopUp(false);
      }
      if (editingStarter) {
        const activeStarterRef = starterRefs.current[editingStarter];
        if (activeStarterRef && !activeStarterRef.contains(event.target as Node)) {
          setEditingStarter(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingPopUp, editingStarter]);
  const handleStarterEdit = (id: string, value: string) => {
    setEditingStarter(id);
    setEditedStarters(prev => [...prev.filter(s => s.id !== id), { id, value }]);
  };

  const handleStarterSave = async (id: string) => {
    try {
      await put(`/pages/${pageId}/starters/${id}`, { value: editedStarters.find(s => s.id === id)?.value });
      setPage((prev: any) => ({
        ...prev,
        page_config: {
          ...prev.page_config,
          starters: prev.page_config.starters.map((s: any) =>
            s.id === id ? { ...s, value: editedStarters.find(es => es.id === id)?.value || s.value } : s
          )
        }
      }));
      setEditingStarter(null);
      toast.success('Starter updated successfully', toastConfig);
    } catch (error) {
      console.error('Error updating starter:', error);
      toast.error('Error updating starter', toastConfig);
    }
  };

  const handlePopUpEdit = () => {
    setEditingPopUp(true);
    setEditedPopUp(page?.page_config?.pop_up);
  };

  const handlePopUpSave = async () => {
    try {
      await put(`/pages/${pageId}/popup/`, { pop_up: editedPopUp });
      setPage((prev: any) => ({
        ...prev,
        page_config: {
          ...prev.page_config,
          pop_up: editedPopUp
        }
      }));
      setEditingPopUp(false);
      toast.success('Pop-up message updated successfully', toastConfig);
    } catch (error) {
      console.error('Error updating pop-up message:', error);
      toast.error('Error updating pop-up message', toastConfig);
    }
  };

  const renderChunks = (chunks: any) => {
    return (
      <ScrollArea className="max-h-[30vh] w-full">
        <div className="space-y-4 p-1">
          {chunks.map((chunk: any, index: number) => (
            <div 
              key={chunk.id || index} 
              className="rounded-md bg-secondary/20 p-4 border border-border"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Chunk {index + 1} • {chunk.tokens} tokens
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  ID: {chunk.id ? chunk.id.substring(0, 8) + '...' : 'N/A'}
                </span>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm">
                {chunk.text}
              </pre>
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  };

  const renderAnchorTags = (anchorTags: Array<{ url: string; name: string }>) => {
    return (
      <div className="max-h-[200px] w-full border rounded-md overflow-y-auto">
        <div className="space-y-1 p-2">
          {anchorTags.map((tag, index) => (
            <div 
              key={index} 
              className="flex items-center text-xs py-2 px-2 border-b border-dashed border-border hover:bg-accent/50 transition-colors rounded-sm"
            >
              <ExternalLink className="w-3 h-3 mr-2 text-slate-400 flex-shrink-0 truncate" />
              <a 
                href={tag.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline flex-1 mr-2 break-all"
                onClick={(e) => e.stopPropagation()}
                title={tag.name}
              >
                {tag.name}
              </a>
              <a 
                href={tag.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-primary flex-shrink-0 break-all"
                onClick={(e) => e.stopPropagation()}
                title="Open link"
              >
                <Globe className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => !isOpen && closeModal()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto scrollbar-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl text-center">Page Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 min-h-[50vh] relative">
          {loading ? (
            <div className="absolute inset-0 flex justify-center items-center bg-background/60 z-10">
              <div className="animate-spin h-5 w-5 border-2 border-slate-500 rounded-full border-t-transparent"></div>
            </div>
          ) : (
            page && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">URL</Label>
                  <p className="mt-1 text-sm">{page.url}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <p className="mt-1 text-sm">{page.title}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Meta Description</Label>
                  <p className="mt-1 text-sm">{page.meta_description}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Last Trained</Label>
                  <p className="mt-1 text-sm">{page.indexed_at ? formatDate(page.indexed_at) : ''}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-1">Pop-up Message</Label>
                  <div ref={popUpRef}>
                    {editingPopUp ? (
                      <div className="flex items-center rounded-lg border bg-background p-2 mt-1 text-sm">
                        <Input
                          type="text"
                          value={editedPopUp}
                          onChange={(e) => setEditedPopUp(e.target.value)}
                          className="flex-grow text-sm"
                        />
                        <Button
                          onClick={handlePopUpSave}
                          size="sm"
                          className="ml-2"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center rounded-lg border bg-background p-2 mt-1 text-sm">
                        <p className="flex-grow text-sm">{page?.page_config?.pop_up ?? 'Re-index to automatically generate pop-up message'}</p>
                        <Button
                          onClick={handlePopUpEdit}
                          size="sm"
                          variant="outline"
                          className="ml-2"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-1">Starters</Label>
                  {page?.page_config?.starters?.length > 0 ? (
                    <ul className="space-y-2">
                      {page?.page_config?.starters?.map((starter: { id: string; value: string }) => (
                        <li
                          key={starter.id}
                          ref={(el) => starterRefs.current[starter.id] = el}
                          className="flex items-center rounded-lg border bg-background p-2 mt-1 text-sm"
                        >
                          {editingStarter === starter.id ? (
                            <>
                              <Input
                                type="text"
                                value={editedStarters.find(s => s.id === starter.id)?.value || starter.value}
                                onChange={(e) => handleStarterEdit(starter.id, e.target.value)}
                                className="flex-grow text-sm"
                              />
                              <Button
                                onClick={() => handleStarterSave(starter.id)}
                                size="sm"
                                className="ml-2"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="flex-grow text-sm">{starter.value}</span>
                              <Button
                                onClick={() => handleStarterEdit(starter.id, starter.value)}
                                size="sm"
                                variant="outline"
                                className="ml-2"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm">Re-index to automatically generate starters</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Current Page Only</Label>
                  <p className="mt-1 text-sm">
                    {page.page_config?.current_page_only ? 'Yes' : 'No'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Extracted URLs ({page?.page_data?.anchor_tags?.length || 0})</Label>
                  <div className="mt-1">
                    {page?.page_data?.anchor_tags?.length > 0 ? (
                      renderAnchorTags(page.page_data.anchor_tags)
                    ) : (
                      <p className="text-sm">No URLs extracted</p>
                    )}
                  </div>
                </div>
                
              </div>
            )
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={closeModal}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PageModal;